import type {
  HoldingSnapshot,
  SlimeAppearance,
  SlimePaletteId,
} from "./types";

export interface SlimePalette {
  id: SlimePaletteId;
  bodyColor: string;
  accentColor: string;
}

export const SLIME_PALETTES: readonly SlimePalette[] = [
  { id: "leaf", bodyColor: "#72CF54", accentColor: "#32934D" },
  { id: "lime", bodyColor: "#A8D94F", accentColor: "#668D2D" },
  { id: "lemon", bodyColor: "#D8D94C", accentColor: "#8F9130" },
  { id: "sky", bodyColor: "#70C9E8", accentColor: "#357EA5" },
  { id: "aqua", bodyColor: "#56D5DD", accentColor: "#258895" },
  { id: "cobalt", bodyColor: "#568AE6", accentColor: "#3558A5" },
  { id: "denim", bodyColor: "#6683B5", accentColor: "#405C8A" },
  { id: "coral", bodyColor: "#F47F72", accentColor: "#B84A5B" },
  { id: "cherry", bodyColor: "#DD5B66", accentColor: "#9E3443" },
  { id: "rose", bodyColor: "#E56891", accentColor: "#A53E64" },
  { id: "honey", bodyColor: "#F2C84B", accentColor: "#C07835" },
  { id: "mango", bodyColor: "#F3B547", accentColor: "#B8732B" },
  { id: "tangerine", bodyColor: "#F18A42", accentColor: "#B6522E" },
  { id: "violet", bodyColor: "#A88BE8", accentColor: "#6754A7" },
  { id: "indigo", bodyColor: "#6C70D9", accentColor: "#454A9C" },
  { id: "grape", bodyColor: "#8765C5", accentColor: "#5A3D91" },
  { id: "orchid", bodyColor: "#C06FD1", accentColor: "#814690" },
  { id: "mint", bodyColor: "#62D3B5", accentColor: "#288C7A" },
  { id: "teal", bodyColor: "#39B8A4", accentColor: "#227D72" },
  { id: "lagoon", bodyColor: "#3EA3AE", accentColor: "#286E7A" },
  { id: "berry", bodyColor: "#E97BAE", accentColor: "#A23E78" },
  { id: "apricot", bodyColor: "#F1A65F", accentColor: "#B85E3D" },
  { id: "forest", bodyColor: "#4DAB6C", accentColor: "#2E7749" },
  { id: "moss", bodyColor: "#8AAD55", accentColor: "#5B7538" },
  { id: "cocoa", bodyColor: "#B87962", accentColor: "#7D4B3C" },
] as const;

/**
 * These eight palettes have authored AI animation atlases. New presets use
 * the master green atlas plus the selective body shader until a baked atlas
 * is explicitly added for them.
 */
export const BAKED_SLIME_PALETTE_IDS = [
  "leaf",
  "sky",
  "coral",
  "honey",
  "violet",
  "mint",
  "berry",
  "apricot",
] as const satisfies readonly SlimePaletteId[];

/**
 * New holdings walk this visually varied order and persist the chosen id.
 * Existing uncustomized holdings keep the original eight-color hash below.
 */
export const SLIME_REGISTRATION_PALETTE_IDS = [
  "leaf",
  "sky",
  "coral",
  "honey",
  "violet",
  "mint",
  "berry",
  "apricot",
  "cobalt",
  "lime",
  "rose",
  "lemon",
  "indigo",
  "teal",
  "cherry",
  "mango",
  "denim",
  "forest",
  "orchid",
  "tangerine",
  "lagoon",
  "moss",
  "aqua",
  "grape",
  "cocoa",
] as const satisfies readonly SlimePaletteId[];

export function registrationPaletteIdForSlot(
  slot: number,
  unavailable: ReadonlySet<SlimePaletteId>,
): SlimePaletteId {
  const normalizedSlot =
    Math.max(0, Math.trunc(slot)) % SLIME_REGISTRATION_PALETTE_IDS.length;

  for (
    let offset = 0;
    offset < SLIME_REGISTRATION_PALETTE_IDS.length;
    offset += 1
  ) {
    const paletteId =
      SLIME_REGISTRATION_PALETTE_IDS[
        (normalizedSlot + offset) % SLIME_REGISTRATION_PALETTE_IDS.length
      ];
    if (!unavailable.has(paletteId)) {
      return paletteId;
    }
  }

  return SLIME_REGISTRATION_PALETTE_IDS[normalizedSlot];
}

/** Platform-independent FNV-1a hash, returned as an unsigned 32-bit integer. */
export function stableSeed(value: string): number {
  let hash = 0x811c9dc5;
  const normalized = value.normalize("NFKC");

  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
}

export function normalizeTicker(ticker: string): string {
  return ticker.trim().toUpperCase().replace(/\s+/g, "");
}

export function tickerSeed(ticker: string): number {
  return stableSeed(normalizeTicker(ticker));
}

/**
 * Persisted holdings own their character identity independently of ticker
 * edits. Legacy/demo snapshots intentionally keep the historical ticker seed.
 */
export function holdingSeed(
  holding: Pick<HoldingSnapshot, "instrumentId" | "ticker">,
): number {
  return holding.instrumentId.startsWith("holding:")
    ? stableSeed(holding.instrumentId)
    : tickerSeed(holding.ticker);
}

export function paletteById(id: SlimePaletteId): SlimePalette {
  return (
    SLIME_PALETTES.find((palette) => palette.id === id) ?? SLIME_PALETTES[0]
  );
}

export function defaultAppearanceForHolding(
  holding: Pick<HoldingSnapshot, "instrumentId" | "ticker">,
): SlimeAppearance {
  const seed = holdingSeed(holding);
  const palette = paletteById(
    BAKED_SLIME_PALETTE_IDS[seed % BAKED_SLIME_PALETTE_IDS.length],
  );

  return {
    paletteId: palette.id,
    bodyColor: palette.bodyColor,
    accentColor: palette.accentColor,
    // The existing stripe/speckle renderer is not clipped to the animated
    // body mask, so automatic patterns look like stray lines or square noise.
    // Keep pattern ids in the schema for a future masked customization pass,
    // but default residents remain visually clean.
    patternId: "plain",
  };
}
